import React, { useState, useEffect } from 'react';
import { Upload, Check, AlertCircle, Paperclip, AlertTriangle } from 'lucide-react';
import {
  ParsedTransaction,
  CDBData,
  DASData,
  PayrollData,
  FGTSRelatorioData,
  DARFFederalData,
  FGTSGuiaData,
  LiquidosData,
  getDeclaredAccounts,
  parseImportFile,
  commitImport,
  commitCDBImport,
  commitCEFLoanImport,
  commitPayrollImport,
  validatePayroll,
} from '../services/apiService';
import { uploadDocument } from '../services/documentService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

type Step = 'upload' | 'preview' | 'preview-cdb' | 'preview-das' | 'preview-payroll' | 'preview-fgts' | 'preview-darf' | 'preview-fgts-guia' | 'preview-liquidos' | 'preview-cef-loan' | 'preview-batch' | 'done';

const BRAZILIAN_PREPOSITIONS = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);

function formatBrazilianName(name: string): string {
  return name.split(' ').map((w, i) => {
    const lower = w.toLowerCase();
    if (i > 0 && BRAZILIAN_PREPOSITIONS.has(lower)) return lower;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');
}

const PAYROLL_FORMATS = ['pdf_holerite', 'pdf_fgts_relatorio', 'pdf_darf_federal', 'pdf_fgts_guia', 'pdf_liquidos', 'pdf_auto', 'pdf_cef_loan'];

export default function ImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState('csv');
  const [sourceAccount, setSourceAccount] = useState('');
  const [accounts, setAccounts] = useState<string[]>([]);

  // CSV options
  const [separator, setSeparator] = useState(',');
  const [dateCol, setDateCol] = useState(0);
  const [descCol, setDescCol] = useState(1);
  const [amountCol, setAmountCol] = useState(2);
  const [dateFormat, setDateFormat] = useState('%d/%m/%Y');
  const [skipHeader, setSkipHeader] = useState(true);

  // Parsed transactions with categories
  const [transactions, setTransactions] = useState<(ParsedTransaction & { category: string; skip: boolean; duplicate_of: string })[]>([]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ created: number; errors: { index: number; error: string }[] } | null>(null);
  const [employeeSync, setEmployeeSync] = useState<{ created: any[]; updated: any[]; deactivated: any[] } | null>(null);
  const [saldo, setSaldo] = useState<{ saldo_anterior: number; saldo_final_pdf: number | null; saldo_final_calculado: number; ok: boolean } | null>(null);
  const [cdbData, setCdbData] = useState<CDBData | null>(null);
  const [cdbReplace, setCdbReplace] = useState(false);
  const [dasData, setDasData] = useState<DASData | null>(null);
  const [dasReplace, setDasReplace] = useState(false);
  const [docAttached, setDocAttached] = useState<boolean | null>(null);
  const [docAttachError, setDocAttachError] = useState('');

  // Payroll state
  const [payrollData, setPayrollData] = useState<PayrollData | null>(null);
  const [payrollSkip, setPayrollSkip] = useState<Record<string, boolean>>({});
  const [payrollWarnings, setPayrollWarnings] = useState<string[]>([]);
  const [fgtsData, setFgtsData] = useState<FGTSRelatorioData | null>(null);
  const [darfData, setDarfData] = useState<DARFFederalData | null>(null);
  const [fgtsGuiaData, setFgtsGuiaData] = useState<FGTSGuiaData | null>(null);
  const [liquidosData, setLiquidosData] = useState<LiquidosData | null>(null);
  const [cefLoanData, setCefLoanData] = useState<any>(null);

  // Batch upload state
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchPayroll, setBatchPayroll] = useState<PayrollData | null>(null);
  const [batchPayrollSkip, setBatchPayrollSkip] = useState<Record<string, boolean>>({});
  const [batchFgts, setBatchFgts] = useState<FGTSRelatorioData | null>(null);
  const [batchDarf, setBatchDarf] = useState<DARFFederalData | null>(null);
  const [batchFgtsGuia, setBatchFgtsGuia] = useState<FGTSGuiaData | null>(null);
  const [batchLiquidos, setBatchLiquidos] = useState<LiquidosData | null>(null);
  const [batchFileMap, setBatchFileMap] = useState<Record<string, File>>({});
  const [batchWarnings, setBatchWarnings] = useState<string[]>([]);

  // Category accounts (all except the source account itself)
  const categoryAccounts = accounts.filter(
    (a) => a !== sourceAccount && !a.startsWith('Patrimônio:Lucros') && a !== 'Ativos' && a !== 'Despesas' && a !== 'Receitas' && a !== 'Patrimônio' && a !== 'Passivo'
  );

  // Source accounts (Ativos:Banco:*)
  const sourceAccounts = accounts.filter((a) => a.startsWith('Ativos:'));

  useEffect(() => {
    getDeclaredAccounts()
      .then((res) => {
        setAccounts(res.accounts);
        const defaultSource = res.accounts.find((a) => a.startsWith('Ativos:Banco'));
        if (defaultSource) setSourceAccount(defaultSource);
      })
      .catch(console.error);
  }, []);

  const handleSingleParseResult = (data: any) => {
    if (data.das) {
      setDasData(data.das);
      setDasReplace(!!data.duplicate_of);
      setStep('preview-das');
    } else if (data.cdb) {
      setCdbData(data.cdb);
      setCdbReplace(!!data.duplicate_of);
      setStep('preview-cdb');
    } else if (data.payroll) {
      setPayrollData(data.payroll);
      setPayrollSkip(Object.fromEntries(data.payroll.funcionarios.map((f: any) => [f.nome, false])));
      setPayrollWarnings(data.warnings || []);
      setStep('preview-payroll');
    } else if (data.fgts_relatorio) {
      setFgtsData(data.fgts_relatorio);
      setPayrollWarnings(data.warnings || []);
      setStep('preview-fgts');
    } else if (data.darf_federal) {
      setDarfData(data.darf_federal);
      setPayrollWarnings(data.warnings || []);
      setStep('preview-darf');
    } else if (data.fgts_guia) {
      setFgtsGuiaData(data.fgts_guia);
      setStep('preview-fgts-guia');
    } else if (data.liquidos) {
      setLiquidosData(data.liquidos);
      setStep('preview-liquidos');
    } else if (data.cef_loan) {
      setCefLoanData(data.cef_loan);
      setStep('preview-cef-loan');
    } else {
      setTransactions(data.transactions.map((t: any) => ({
        ...t,
        category: t.suggested_category || '',
        duplicate_of: t.duplicate_of || '',
        skip: !!t.duplicate_of,
      })));
      setSaldo(data.saldo || null);
      setStep('preview');
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      // Batch mode: parse multiple payroll PDFs
      if (batchFiles.length > 1) {
        let bPayroll: PayrollData | null = null;
        let bFgts: FGTSRelatorioData | null = null;
        let bDarf: DARFFederalData | null = null;
        let bFgtsGuia: FGTSGuiaData | null = null;
        let bLiquidos: LiquidosData | null = null;
        const allWarnings: string[] = [];
        const fileMap: Record<string, File> = {};

        for (const f of batchFiles) {
          const useFormat = format === 'pdf_auto' ? 'pdf_auto' : detectPdfFormat(f.name);
          const data = await parseImportFile(f, { format: useFormat }) as any;
          allWarnings.push(...(data.warnings || []));
          if (data.payroll) {
            bPayroll = data.payroll;
            fileMap['payroll'] = f;
          } else if (data.fgts_relatorio) {
            bFgts = data.fgts_relatorio;
            fileMap['fgts'] = f;
          } else if (data.darf_federal) {
            bDarf = data.darf_federal;
            fileMap['darf'] = f;
          } else if (data.fgts_guia) {
            bFgtsGuia = data.fgts_guia;
            fileMap['fgts_guia'] = f;
          } else if (data.liquidos) {
            bLiquidos = data.liquidos;
            fileMap['liquidos'] = f;
          }
        }

        // Cross-validate
        try {
          const validation = await validatePayroll({
            holerite: bPayroll,
            fgts_relatorio: bFgts,
            liquidos: bLiquidos,
            fgts_guia: bFgtsGuia,
          });
          allWarnings.push(...validation.warnings);
        } catch {
          // Non-blocking
        }

        setBatchPayroll(bPayroll);
        setBatchPayrollSkip(bPayroll ? Object.fromEntries(bPayroll.funcionarios.map(f => [f.nome, false])) : {});
        setBatchFgts(bFgts);
        setBatchDarf(bDarf);
        setBatchFgtsGuia(bFgtsGuia);
        setBatchLiquidos(bLiquidos);
        setBatchFileMap(fileMap);
        setBatchWarnings(allWarnings);
        setStep('preview-batch');
      } else {
        // Single file mode
        const res = await parseImportFile(file, {
          format,
          separator: format === 'csv' ? separator : undefined,
          date_col: format === 'csv' ? dateCol : undefined,
          description_col: format === 'csv' ? descCol : undefined,
          amount_col: format === 'csv' ? amountCol : undefined,
          date_format: format === 'csv' ? dateFormat : undefined,
          skip_header: format === 'csv' ? skipHeader : undefined,
          source_account: sourceAccount,
        });
        handleSingleParseResult(res);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao parsear arquivo');
    } finally {
      setParsing(false);
    }
  };

  const handleCategoryChange = (index: number, category: string) => {
    setTransactions((prev) =>
      prev.map((t, i) => (i === index ? { ...t, category } : t))
    );
  };

  const handleSetAllCategories = (category: string) => {
    setTransactions((prev) => prev.map((t) => (t.category || t.skip ? t : { ...t, category })));
  };

  const handleToggleSkip = (index: number) => {
    setTransactions((prev) =>
      prev.map((t, i) => (i === index ? { ...t, skip: !t.skip } : t))
    );
  };

  const handleCommit = async () => {
    const toImport = transactions.filter((t) => !t.skip);
    const uncategorized = toImport.filter((t) => !t.category);
    if (uncategorized.length > 0) {
      setError(`${uncategorized.length} transação(ões) sem conta`);
      return;
    }
    if (!sourceAccount) {
      setError('Selecione uma conta de origem');
      return;
    }

    setCommitting(true);
    setError('');
    try {
      const res = await commitImport(
        toImport.map((t) => ({
          date: t.date,
          description: t.description,
          amount: t.amount,
          category: t.category,
          source_account: sourceAccount,
        }))
      );
      setResult(res);
      setStep('done');
      // Auto-attach source file to first imported transaction
      if (file && toImport.length > 0) {
        try {
          const firstTxn = toImport[0];
          const txnDate = firstTxn.date.replace(/\//g, '-');
          const docType = format === 'ofx' ? 'extrato' : format === 'pdf_caixa' ? 'extrato' : 'comprovante';
          const desc = format === 'ofx' ? `Extrato OFX ${file.name}` : format === 'pdf_caixa' ? 'Extrato Caixa' : `Importação ${file.name}`;
          await uploadDocument(file, docType, desc, txnDate, firstTxn.description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Transações importadas, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar transações');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitDAS = async () => {
    if (!dasData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitCDBImport(dasData.transaction);
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      // Auto-attach the source PDF
      if (file) {
        try {
          const txnDate = dasData.transaction.date.replace(/\//g, '-');
          await uploadDocument(
            file, 'darf',
            `DAS Simples Nacional ${dasData.periodo}`,
            txnDate,
            dasData.transaction.description,
          );
          setDocAttached(true);
        } catch {
          setDocAttachError('Transação importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar DAS');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitCDB = async () => {
    if (!cdbData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitCDBImport(cdbData.transaction);
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      // Auto-attach the source PDF
      if (file) {
        try {
          const txnDate = cdbData.transaction.date.replace(/\//g, '-');
          await uploadDocument(
            file, 'comprovante',
            `Comprovante resgate CDB ${cdbData.produto}`,
            txnDate,
            cdbData.transaction.description,
          );
          setDocAttached(true);
        } catch {
          setDocAttachError('Transação importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar transação CDB');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitPayroll = async () => {
    if (!payrollData) return;
    const skippedNames = new Set(Object.entries(payrollSkip).filter(([, v]) => v).map(([k]) => k));
    const txnsToCommit = payrollData.transactions.filter((txn, i) => {
      const func = payrollData.funcionarios[i];
      return func ? !skippedNames.has(func.nome) : true;
    });
    if (txnsToCommit.length === 0) {
      setError('Nenhuma transação selecionada');
      return;
    }
    setCommitting(true);
    setError('');
    try {
      const funcsToCommit = payrollData.funcionarios.filter(f => !skippedNames.has(f.nome));
      const res = await commitPayrollImport({
        competencia: payrollData.competencia,
        transactions: txnsToCommit,
        funcionarios: funcsToCommit,
      });
      setResult({ created: res.created, errors: res.errors });
      if ((res as any).employee_sync) {
        setEmployeeSync((res as any).employee_sync);
      }
      setStep('done');
      if (file) {
        try {
          const txnDate = txnsToCommit[0].date.replace(/\//g, '-');
          await uploadDocument(file, 'folha', `Holerite ${payrollData.competencia}`, txnDate, txnsToCommit[0].description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisões importadas, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisões');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitFGTS = async () => {
    if (!fgtsData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitPayrollImport({
        competencia: fgtsData.competencia,
        transactions: [fgtsData.transaction],
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      if (file) {
        try {
          const txnDate = fgtsData.transaction.date.replace(/\//g, '-');
          await uploadDocument(file, 'fgts', `Relatório FGTS ${fgtsData.competencia}`, txnDate, fgtsData.transaction.description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisão importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisão FGTS');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitDARF = async () => {
    if (!darfData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitPayrollImport({
        competencia: darfData.competencia,
        transactions: [darfData.transaction],
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      if (file) {
        try {
          const txnDate = darfData.transaction.date.replace(/\//g, '-');
          await uploadDocument(file, 'darf', `DARF Tributos Federais ${darfData.competencia}`, txnDate, darfData.transaction.description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisão importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisão tributos');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitBatch = async () => {
    setCommitting(true);
    setError('');
    try {
      // Collect all transactions to commit in a single batch
      const allTransactions: { date: string; description: string; postings: { account: string; amount: number; commodity: string }[] }[] = [];
      let competencia = '';

      if (batchPayroll) {
        competencia = batchPayroll.competencia;
        const skippedNames = new Set(Object.entries(batchPayrollSkip).filter(([, v]) => v).map(([k]) => k));
        batchPayroll.transactions.forEach((txn, i) => {
          const func = batchPayroll.funcionarios[i];
          if (!func || !skippedNames.has(func.nome)) allTransactions.push(txn);
        });
      }
      if (batchFgts) {
        if (!competencia) competencia = batchFgts.competencia;
        allTransactions.push(batchFgts.transaction);
      }
      if (batchDarf) {
        if (!competencia) competencia = batchDarf.competencia;
        allTransactions.push(batchDarf.transaction);
      }

      if (allTransactions.length === 0) {
        setError('Nenhuma transação para importar');
        setCommitting(false);
        return;
      }

      const res = await commitPayrollImport({
        competencia,
        transactions: allTransactions,
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');

      // Attach source PDFs as documents
      try {
        const txnDate = allTransactions[0]?.date?.replace(/\//g, '-') || '';
        const docUploads: Promise<any>[] = [];
        if (batchFileMap['payroll'] && batchPayroll) {
          docUploads.push(uploadDocument(batchFileMap['payroll'], 'folha', `Holerite ${competencia}`, txnDate, allTransactions[0]?.description || ''));
        }
        if (batchFileMap['fgts'] && batchFgts) {
          docUploads.push(uploadDocument(batchFileMap['fgts'], 'fgts', `Relatório FGTS ${competencia}`, txnDate, batchFgts.transaction.description));
        }
        if (batchFileMap['darf'] && batchDarf) {
          docUploads.push(uploadDocument(batchFileMap['darf'], 'darf', `DARF Tributos Federais ${competencia}`, txnDate, batchDarf.transaction.description));
        }
        if (docUploads.length > 0) {
          await Promise.all(docUploads);
          setDocAttached(true);
        }
      } catch {
        setDocAttachError('Provisões importadas, mas falha ao anexar documentos');
        setDocAttached(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisões');
    } finally {
      setCommitting(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setTransactions([]);
    setCdbData(null);
    setCdbReplace(false);
    setDasData(null);
    setDasReplace(false);
    setPayrollData(null);
    setPayrollSkip({});
    setPayrollWarnings([]);
    setFgtsData(null);
    setDarfData(null);
    setFgtsGuiaData(null);
    setLiquidosData(null);
    setCefLoanData(null);
    setBatchFiles([]);
    setBatchPayroll(null);
    setBatchPayrollSkip({});
    setBatchFgts(null);
    setBatchDarf(null);
    setBatchFgtsGuia(null);
    setBatchLiquidos(null);
    setBatchFileMap({});
    setBatchWarnings([]);
    setResult(null);
    setEmployeeSync(null);
    setError('');
    setDocAttached(null);
    setDocAttachError('');
  };

  const formatAmount = (amount: number) => {
    const formatted = Math.abs(amount).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount < 0 ? `-R$ ${formatted}` : `R$ ${formatted}`;
  };

  const detectPdfFormat = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('holerite') || lower.includes('recibo') || lower.includes('folha')) return 'pdf_holerite';
    if (lower.includes('fgts') && (lower.includes('relat') || lower.includes('detalh'))) return 'pdf_fgts_relatorio';
    if (lower.includes('darf') || lower.includes('tribut')) return 'pdf_darf_federal';
    if (lower.includes('fgts') && (lower.includes('guia') || lower.includes('gfd'))) return 'pdf_fgts_guia';
    if (lower.includes('liquido') || lower.includes('líquido')) return 'pdf_liquidos';
    if (lower.includes('comprovante') || lower.includes('cdb') || lower.includes('renda fixa')) return 'pdf_cdb_santander';
    if (lower.includes('das') || lower.includes('simples')) return 'pdf_das';
    return 'pdf_caixa';
  };

  if (!accounts.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Importar Transações</h1>
        {step !== 'upload' && (
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Nova importação
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* File upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo</label>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {batchFiles.length > 1
                    ? `${batchFiles.length} arquivos selecionados`
                    : file ? file.name : 'Clique para selecionar (múltiplos PDFs para folha)'}
                </span>
                <input
                  type="file"
                  accept=".csv,.ofx,.qfx,.pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    if (files.length > 1) {
                      // Batch mode: multiple payroll PDFs
                      setBatchFiles(Array.from(files));
                      setFile(files[0]);
                      setFormat('pdf_auto');
                    } else {
                      const f = files[0];
                      setFile(f);
                      setBatchFiles([]);
                      const ext = f.name.split('.').pop()?.toLowerCase();
                      if (ext === 'ofx' || ext === 'qfx') setFormat('ofx');
                      else if (ext === 'pdf') {
                        const detected = detectPdfFormat(f.name);
                        setFormat(detected);
                        // Auto-select source account based on PDF format
                        const formatAccountMap: Record<string, string> = {
                          'pdf_caixa': 'Ativos:Banco:CEF',
                          'pdf_cdb_santander': 'Ativos:Banco:Santander',
                        };
                        const preferred = formatAccountMap[detected];
                        if (preferred) {
                          const match = accounts.find((a) => a === preferred);
                          if (match) setSourceAccount(match);
                        }
                      }
                      else setFormat('csv');
                    }
                  }}
                />
              </label>
            </div>

            {/* Format & source account */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <optgroup label="Extrato Bancário">
                    <option value="csv">CSV</option>
                    <option value="ofx">OFX</option>
                    <option value="pdf_caixa">PDF Caixa</option>
                    <option value="pdf_cdb_santander">PDF CDB Santander</option>
                    <option value="pdf_das">PDF DAS (Simples Nacional)</option>
                  </optgroup>
                  <optgroup label="Folha de Pagamento">
                    <option value="pdf_auto">Auto-detectar (por conteúdo)</option>
                    <option value="pdf_holerite">Holerite (Recibo de Pagamento)</option>
                    <option value="pdf_fgts_relatorio">Relatório FGTS</option>
                    <option value="pdf_darf_federal">DARF Tributos Federais</option>
                    <option value="pdf_fgts_guia">Guia FGTS</option>
                    <option value="pdf_liquidos">Relatório de Líquidos</option>
                  </optgroup>
                  <optgroup label="Empréstimos">
                    <option value="pdf_cef_loan">Evolução de Dívida CEF</option>
                  </optgroup>
                </select>
              </div>
              {!PAYROLL_FORMATS.includes(format) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conta de origem</label>
                  <select
                    value={sourceAccount}
                    onChange={(e) => setSourceAccount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecione...</option>
                    {sourceAccounts.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* CSV options */}
          {format === 'csv' && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Opções CSV</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Separador</label>
                  <select
                    value={separator}
                    onChange={(e) => setSeparator(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value=",">Vírgula (,)</option>
                    <option value=";">Ponto e vírgula (;)</option>
                    <option value="\t">Tab</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Formato da data</label>
                  <select
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="%d/%m/%Y">DD/MM/AAAA</option>
                    <option value="%Y-%m-%d">AAAA-MM-DD</option>
                    <option value="%m/%d/%Y">MM/DD/AAAA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Data</label>
                  <input
                    type="number"
                    min="0"
                    value={dateCol}
                    onChange={(e) => setDateCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Descrição</label>
                  <input
                    type="number"
                    min="0"
                    value={descCol}
                    onChange={(e) => setDescCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Valor</label>
                  <input
                    type="number"
                    min="0"
                    value={amountCol}
                    onChange={(e) => setAmountCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={skipHeader}
                      onChange={(e) => setSkipHeader(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Pular cabeçalho
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleParse}
              disabled={!file || parsing}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {parsing ? <LoadingSpinner /> : <Upload className="h-4 w-4" />}
              Processar arquivo
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview (bank statement) */}
      {step === 'preview' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — {transactions.filter((t) => !t.skip).length}/{transactions.length} transações
              </h2>
              <p className="text-sm text-gray-500">
                Atribua uma conta para cada linha.
                {transactions.filter((t) => t.skip).length > 0 && (
                  <span className="text-amber-600 ml-1">
                    {transactions.filter((t) => t.skip).length} duplicada(s) serão ignoradas.
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                onChange={(e) => {
                  if (e.target.value) handleSetAllCategories(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                defaultValue=""
              >
                <option value="" disabled>Aplicar conta em massa...</option>
                {categoryAccounts.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                Confirmar importação
              </button>
            </div>
          </div>
          {saldo && (
            <div className={`mx-4 mt-3 p-3 rounded-lg text-sm ${saldo.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <span><strong>Saldo anterior:</strong> {formatAmount(saldo.saldo_anterior)}</span>
                {saldo.saldo_final_pdf !== null && (
                  <span><strong>Saldo final (extrato):</strong> {formatAmount(saldo.saldo_final_pdf)}</span>
                )}
                <span><strong>Saldo calculado:</strong> {formatAmount(saldo.saldo_final_calculado)}</span>
                {!saldo.ok && (
                  <span className="font-semibold text-red-700">
                    Saldo não bate — possível transação faltando!
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-3 text-center font-medium w-10">
                    <span title="Importar">Imp.</span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Data</th>
                  <th className="px-4 py-3 text-left font-medium">Descrição</th>
                  <th className="px-4 py-3 text-right font-medium">Valor</th>
                  <th className="px-4 py-3 text-left font-medium">Conta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((txn, i) => (
                  <tr key={i} className={`hover:bg-blue-50 even:bg-gray-100 ${txn.skip ? 'opacity-40' : ''}`}>
                    <td className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={!txn.skip}
                        onChange={() => handleToggleSkip(i)}
                        className="rounded border-gray-300"
                        title={txn.duplicate_of ? `Possível duplicata de: ${txn.duplicate_of}` : 'Importar esta transação'}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{txn.date}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-900">{txn.description}</span>
                      {txn.duplicate_of && (
                        <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                          duplicata: {txn.duplicate_of}
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-2.5 text-right whitespace-nowrap font-mono ${
                      txn.amount < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatAmount(txn.amount)}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={txn.category}
                        onChange={(e) => handleCategoryChange(i, e.target.value)}
                        disabled={txn.skip}
                        className={`w-full px-2 py-1.5 border rounded text-sm ${
                          txn.skip ? 'bg-gray-100 border-gray-200' :
                          txn.category ? 'border-gray-300' : 'border-red-300 bg-red-50'
                        }`}
                      >
                        <option value="">Selecione...</option>
                        {categoryAccounts.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 2b: CDB Preview */}
      {step === 'preview-cdb' && cdbData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — Resgate {cdbData.produto}
              </h2>
              <p className="text-sm text-gray-500">
                Aplicado em {cdbData.data_aplicacao.split('/').reverse().join('/')} — Resgatado em {cdbData.data_resgate.split('/').reverse().join('/')}
              </p>
            </div>
            <button
              onClick={handleCommitCDB}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              {cdbReplace ? 'Substituir transação do extrato' : 'Confirmar importação'}
            </button>
          </div>
          {cdbReplace && (
            <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Transação encontrada no extrato — será substituída com os detalhes do CDB
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Valor Aplicado</p>
                <p className="text-lg font-semibold text-gray-900">{formatAmount(cdbData.valor_aplicado)}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-xs text-green-600">Rendimento Bruto</p>
                <p className="text-lg font-semibold text-green-700">{formatAmount(cdbData.rendimento_bruto)}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs text-red-600">IOF</p>
                <p className="text-lg font-semibold text-red-700">{formatAmount(cdbData.iof)}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs text-red-600">IR Fonte</p>
                <p className="text-lg font-semibold text-red-700">{formatAmount(cdbData.ir)}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-600">Valor Líquido</p>
                <p className="text-lg font-semibold text-blue-700">{formatAmount(cdbData.valor_resgate_liquido)}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cdbData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Step 2c: DAS Preview */}
      {step === 'preview-das' && dasData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — DAS Simples Nacional - {dasData.periodo}
              </h2>
              <p className="text-sm text-gray-500">
                Vencimento: {dasData.data_vencimento.split('/').reverse().join('/')}
                {' — '}Total: {formatAmount(dasData.valor_total)}
              </p>
            </div>
            <button
              onClick={handleCommitDAS}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              {dasReplace ? 'Substituir transação do extrato' : 'Confirmar importação'}
            </button>
          </div>
          {dasReplace && (
            <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Transação encontrada no extrato — será substituída com os detalhes do DAS
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {dasData.tributos.map((t) => (
                <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-600">{t.nome}</p>
                  <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dasData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Preview */}
      {step === 'preview-payroll' && payrollData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão Folha — Competência {payrollData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                {payrollData.funcionarios.length} funcionário(s) — Total bruto: {formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.vencimentos, 0))}
              </p>
            </div>
            <button
              onClick={handleCommitPayroll}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  {payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-3 text-center font-medium w-10">Imp.</th>
                  <th className="px-4 py-3 text-left font-medium">Funcionário</th>
                  <th className="px-4 py-3 text-left font-medium">Tipo</th>
                  <th className="px-4 py-3 text-right font-medium">Vencimentos</th>
                  <th className="px-4 py-3 text-right font-medium">INSS</th>
                  <th className="px-4 py-3 text-right font-medium">IRRF</th>
                  <th className="px-4 py-3 text-right font-medium">Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payrollData.funcionarios.map((func, i) => (
                  <tr key={func.nome} className={`hover:bg-blue-50 even:bg-gray-100 ${payrollSkip[func.nome] ? 'opacity-40' : ''}`}>
                    <td className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={!payrollSkip[func.nome]}
                        onChange={() => setPayrollSkip(prev => ({ ...prev, [func.nome]: !prev[func.nome] }))}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded ${func.tipo === 'socio' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                        {func.tipo === 'socio' ? 'Sócio' : 'CLT'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">{formatAmount(func.vencimentos)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{func.inss > 0 ? formatAmount(func.inss) : '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{func.irrf > 0 ? formatAmount(func.irrf) : '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">{formatAmount(func.liquido)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-2 py-2.5" />
                  <td className="px-4 py-2.5 text-gray-900" colSpan={2}>Total</td>
                  <td className="px-4 py-2.5 text-right font-mono">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.vencimentos, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.inss, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.irrf, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.liquido, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* FGTS Preview */}
      {step === 'preview-fgts' && fgtsData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão FGTS — Competência {fgtsData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                {fgtsData.funcionarios.length} funcionário(s) — Total FGTS: {formatAmount(fgtsData.total_fgts)}
              </p>
            </div>
            <button
              onClick={handleCommitFGTS}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão FGTS
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}</div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Funcionário</th>
                  <th className="px-4 py-3 text-right font-medium">Remuneração</th>
                  <th className="px-4 py-3 text-right font-medium">FGTS (8%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fgtsData.funcionarios.map((func, i) => (
                  <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatAmount(func.remuneracao)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-blue-700">{formatAmount(func.fgts)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2.5 text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right font-mono">{formatAmount(fgtsData.funcionarios.reduce((s, f) => s + f.remuneracao, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-blue-700">{formatAmount(fgtsData.total_fgts)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* DARF Preview */}
      {step === 'preview-darf' && darfData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão Tributos Federais — Competência {darfData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                Total: {formatAmount(darfData.valor_total)}
              </p>
            </div>
            <button
              onClick={handleCommitDARF}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão tributos
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}</div>
              </div>
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {darfData.tributos.map((t) => (
                <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-600">{t.codigo} — {t.nome}</p>
                  <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {darfData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* FGTS Guia Preview (validation only) */}
      {step === 'preview-fgts-guia' && fgtsGuiaData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Guia FGTS — Competência {fgtsGuiaData.competencia}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-xs text-blue-600">Valor a Recolher</p>
              <p className="text-2xl font-semibold text-blue-700">{formatAmount(fgtsGuiaData.valor_total)}</p>
            </div>
            {fgtsGuiaData.data_vencimento && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500">Vencimento</p>
                <p className="text-2xl font-semibold text-gray-900">{fgtsGuiaData.data_vencimento.split('/').reverse().join('/')}</p>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">
            A guia FGTS é apenas para validação — a provisão é gerada pelo Relatório FGTS.
          </p>
        </div>
      )}

      {/* Líquidos Preview (validation only) */}
      {step === 'preview-liquidos' && liquidosData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Relatório de Líquidos — Competência {liquidosData.competencia}
            </h2>
            <p className="text-sm text-gray-500">
              {liquidosData.funcionarios.length} funcionário(s) — Total: {formatAmount(liquidosData.total)}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Código</th>
                  <th className="px-4 py-3 text-left font-medium">Nome</th>
                  <th className="px-4 py-3 text-right font-medium">Valor Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {liquidosData.funcionarios.map((func, i) => (
                  <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-2.5 text-gray-500">{func.codigo}</td>
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">{formatAmount(func.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{formatAmount(liquidosData.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-500">
              O relatório de líquidos é apenas para validação cruzada — as provisões são geradas pelo holerite.
            </p>
          </div>
        </div>
      )}

      {/* CEF Loan Preview */}
      {step === 'preview-cef-loan' && cefLoanData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Evolução de Dívida — {cefLoanData.contrato.produto || 'Contrato CEF'}
            </h2>
            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
              <p>Contrato: {cefLoanData.contrato.numero} — Valor: {formatAmount(cefLoanData.contrato.valor_contrato)}</p>
              <p>
                {cefLoanData.resumo.parcelas_pagas}/{cefLoanData.resumo.parcelas_total} parcelas pagas —{' '}
                Total pago: {formatAmount(cefLoanData.resumo.total_pago)} —{' '}
                Saldo devedor: {formatAmount(cefLoanData.resumo.saldo_devedor)}
              </p>
              <p className="text-xs text-gray-400">
                Amortização: {formatAmount(cefLoanData.resumo.total_amortizacao)} |
                Juros: {formatAmount(cefLoanData.resumo.total_juros)} |
                Encargos: {formatAmount(cefLoanData.resumo.total_encargos)}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium">Parcela</th>
                  <th className="px-3 py-2.5 text-left font-medium">Vencimento</th>
                  <th className="px-3 py-2.5 text-center font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right font-medium">Amortização</th>
                  <th className="px-3 py-2.5 text-right font-medium">Juros</th>
                  <th className="px-3 py-2.5 text-right font-medium">Encargos</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total Pago</th>
                  <th className="px-3 py-2.5 text-right font-medium">Saldo Devedor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cefLoanData.parcelas.map((p: any, i: number) => (
                  <tr key={i} className={`hover:bg-blue-50 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                    <td className="px-3 py-2 text-gray-700 font-medium">{p.numero}</td>
                    <td className="px-3 py-2 text-gray-600">{p.vencimento}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        p.status === 'PG' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-800">{p.status === 'PG' ? formatAmount(p.amortizacao) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-600">{p.status === 'PG' ? formatAmount(p.juros) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{p.encargos > 0 ? formatAmount(p.encargos) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium text-gray-900">{p.status === 'PG' ? formatAmount(p.valor_total_pago) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-600">{p.saldo_devedor > 0 ? formatAmount(p.saldo_devedor) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Reclassifica pagamentos existentes: split amortização vs. juros por parcela.
            </p>
            <button
              onClick={async () => {
                setCommitting(true);
                setError('');
                try {
                  const res = await commitCEFLoanImport({
                    contrato_numero: cefLoanData.contrato.numero,
                    contrato_produto: cefLoanData.contrato.produto,
                    parcelas: cefLoanData.parcelas,
                  });
                  setResult({ created: res.updated, errors: res.errors.map((e: any, i: number) => ({ index: e.parcela, error: e.error })) });
                  setStep('done');
                } catch (err: any) {
                  setError(err.response?.data?.detail || 'Erro ao reclassificar empréstimos');
                } finally {
                  setCommitting(false);
                }
              }}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Reclassificar pagamentos
            </button>
          </div>
        </div>
      )}

      {/* Batch Preview */}
      {step === 'preview-batch' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Importação em Lote — Folha de Pagamento
              </h2>
              <p className="text-sm text-gray-500">
                {batchFiles.length} arquivo(s) processado(s)
                {batchPayroll && ` — Competência ${batchPayroll.competencia}`}
              </p>
            </div>
            <button
              onClick={handleCommitBatch}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar todas as provisões
            </button>
          </div>

          {batchWarnings.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  {batchWarnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              </div>
            </div>
          )}

          {/* Holerite section */}
          {batchPayroll && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-900">Holerite — {batchPayroll.funcionarios.length} funcionário(s)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-2 py-2 text-center font-medium w-10">Imp.</th>
                      <th className="px-3 py-2 text-left font-medium">Funcionário</th>
                      <th className="px-3 py-2 text-right font-medium">Vencimentos</th>
                      <th className="px-3 py-2 text-right font-medium">INSS</th>
                      <th className="px-3 py-2 text-right font-medium">IRRF</th>
                      <th className="px-3 py-2 text-right font-medium">Líquido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchPayroll.funcionarios.map((func, i) => (
                      <tr key={func.nome} className={`hover:bg-blue-50 even:bg-gray-100 ${batchPayrollSkip[func.nome] ? 'opacity-40' : ''}`}>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" checked={!batchPayrollSkip[func.nome]}
                            onChange={() => setBatchPayrollSkip(prev => ({ ...prev, [func.nome]: !prev[func.nome] }))}
                            className="rounded border-gray-300" />
                        </td>
                        <td className="px-3 py-2 text-gray-900">{formatBrazilianName(func.nome)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatAmount(func.vencimentos)}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{func.inss > 0 ? formatAmount(func.inss) : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{func.irrf > 0 ? formatAmount(func.irrf) : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-green-700">{formatAmount(func.liquido)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FGTS section */}
          {batchFgts && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-900">FGTS — Total: {formatAmount(batchFgts.total_fgts)}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Funcionário</th>
                      <th className="px-3 py-2 text-right font-medium">Remuneração</th>
                      <th className="px-3 py-2 text-right font-medium">FGTS (8%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchFgts.funcionarios.map((func, i) => (
                      <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                        <td className="px-3 py-2 text-gray-900">{formatBrazilianName(func.nome)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatAmount(func.remuneracao)}</td>
                        <td className="px-3 py-2 text-right font-mono text-blue-700">{formatAmount(func.fgts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* DARF section */}
          {batchDarf && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-red-50">
                <h3 className="text-sm font-semibold text-red-900">DARF Tributos — Total: {formatAmount(batchDarf.valor_total)}</h3>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                {batchDarf.tributos.map((t) => (
                  <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs text-red-600">{t.codigo} — {t.nome}</p>
                    <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FGTS Guia validation */}
          {batchFgtsGuia && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Guia FGTS (validação)</h3>
              <p className="text-sm text-gray-500 mt-1">Valor a recolher: {formatAmount(batchFgtsGuia.valor_total)}</p>
            </div>
          )}

          {/* Líquidos validation */}
          {batchLiquidos && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Relatório de Líquidos (validação)</h3>
              <p className="text-sm text-gray-500 mt-1">{batchLiquidos.funcionarios.length} funcionário(s) — Total: {formatAmount(batchLiquidos.total)}</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Done */}
      {step === 'done' && result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Importação concluída</h2>
          <p className="text-gray-600 mb-1">
            {result.created} transação(ões) importada(s) com sucesso.
          </p>
          <p className="text-sm text-gray-500">
            As transações foram marcadas como pendentes (!) para conciliação posterior.
          </p>
          {(result as any).skipped?.length > 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {(result as any).skipped.length} transação(ões) ignorada(s) (já existiam).
            </p>
          )}
          {docAttached === true && (
            <p className="text-sm text-green-600 mt-1 flex items-center justify-center gap-1">
              <Paperclip className="h-3.5 w-3.5" />
              Documento fonte anexado automaticamente.
            </p>
          )}
          {docAttached === false && docAttachError && (
            <p className="text-sm text-amber-600 mt-1 flex items-center justify-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {docAttachError}
            </p>
          )}
          {employeeSync && (employeeSync.created.length > 0 || employeeSync.updated.length > 0 || employeeSync.deactivated.length > 0) && (
            <div className="mt-4 text-left bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-700 mb-2">Cadastro de funcionários atualizado:</p>
              {employeeSync.created.length > 0 && (
                <p className="text-sm text-blue-600">
                  {employeeSync.created.length} novo(s): {employeeSync.created.map((e: any) => e.name).join(', ')}
                </p>
              )}
              {employeeSync.updated.length > 0 && (
                <p className="text-sm text-blue-600">
                  {employeeSync.updated.length} atualizado(s): {employeeSync.updated.map((e: any) => e.id).join(', ')}
                </p>
              )}
              {employeeSync.deactivated.length > 0 && (
                <p className="text-sm text-amber-600">
                  {employeeSync.deactivated.length} desativado(s): {employeeSync.deactivated.map((e: any) => e.name).join(', ')}
                </p>
              )}
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="mt-4 text-left bg-red-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-2">
                {result.errors.length} erro(s):
              </p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-sm text-red-600">
                  Linha {e.index + 1}: {e.error}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
